import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isRTL, SUPPORTED_LANGUAGES } from '../utils/translations';
import { useMarket } from '../context/MarketContext';

const resolveCurrentLang = () => {
    if (typeof window === 'undefined') return 'ar';
    try {
        const params = new URLSearchParams(window.location.search);
        const urlLang = params.get('lang');
        if (urlLang) return urlLang.toLowerCase();

        const explicit = localStorage.getItem('luxvono_user_explicit_language');
        if (explicit) return explicit;

        const geoStr = localStorage.getItem('luxvono_geo_context');
        if (geoStr) {
            const geo = JSON.parse(geoStr);
            if (geo && geo.language) return geo.language;
        }
    } catch (_) {}
    return 'ar';
};

const resolveCurrentCountry = () => {
    if (typeof window === 'undefined') return 'SA';
    try {
        const params = new URLSearchParams(window.location.search);
        const urlCountry = params.get('country');
        if (urlCountry) return urlCountry.toUpperCase();

        const explicit = localStorage.getItem('luxvono_user_country');
        if (explicit) return explicit;

        const geoStr = localStorage.getItem('luxvono_geo_context');
        if (geoStr) {
            const geo = JSON.parse(geoStr);
            if (geo && geo.countryCode) return geo.countryCode;
        }
    } catch (_) {}
    return 'SA';
};

/**
 * High-End Multilingual SEO Engine for LuxVono
 * Dynamically synchronizes document title, meta tags, OpenGraph, hreflang, and JSON-LD schema
 * across all 14 supported languages and global locales.
 */

const SEO_DICTIONARY = {
    ar: {
        siteName: 'لوكسفونو | المنصة الملكية للتسوق الفاخر',
        homeTitle: 'لوكسفونو | المنصة الملكية للتسوق الفاخر والمقتنيات النادرة | LuxVono',
        homeDesc: 'تسوق أفخم الساعات السويسرية، المجوهرات، الحقائب الحصرية، العطور الملكية، والأزياء الراقية مع خدمة المساعد الشخصي VIP والتوصيل الموثوق لكافة دول الخليج والعالم.',
        homeKeywords: 'لوكسفونو, تسوق فاخر, ساعات فاخرة, مجوهرات, عطور ملكية, حقائب ماركات, تسوق السعودية, تسوق الإمارات, تسوق الخليج, LuxVono',
        videosTitle: 'مسرح البث وعروض الفيديو الفاخرة | لوكسفونو',
        videosDesc: 'شاهد واستعرض أحدث مقاطع الفيديو الحصرية للمنتجات الفاخرة واقتنِ مشترياتك مباشرة من الفيديو.',
        vipTitle: 'صالة كبار الشخصيات VIP والخدمات الحصرية | لوكسفونو',
        vipDesc: 'خدمات استثنائية لكبار الشخصيات والمقتنين المتميزين مع مستشار خاص لتلبية أرقى الطلبات الخاصة.',
        liveTitle: 'منصة البث المباشر الفاخر والتفاعل الحي | لوكسفونو',
        liveDesc: 'انضم إلى جلسات البث الحي الحصرية وتفاعل مع خبراء الفخامة والمقتنيات النادرة.',
        sourcingTitle: 'البحث وتأمين المقتنيات النادرة حسب الطلب | لوكسفونو',
        sourcingDesc: 'خدمة البحث الحصري لتوفير أندر الساعات والمجوهرات والقطع الفاخرة من حول العالم.',
        magazineTitle: 'مجلة لوكسفونو الفاخرة والأخبار الملكية | The Chronicle',
        magazineDesc: 'أحدث مقالات عالم الفخامة والساعات والمجوهرات والموضة الراقية.',
        downloadTitle: 'تحميل تطبيق لوكسفونو للهواتف الذكية (Android & iOS) | LuxVono',
        downloadDesc: 'حمّل تطبيق لوكسفونو واستمتع بتجربة تسوق ملكية سريعة مع إشعارات العروض الحصرية.',
        vendorTitle: 'تسجيل مورد معتمد / تاجر فاخر | لوكسفونو',
        vendorDesc: 'انضم كتاجر ومورد معتمد واعرض منتجاتك الفاخرة لعملاء النخبة حول العالم.',
        contactTitle: 'تواصل مع الكونسيرج والمساعد الشخصي | لوكسفونو',
        contactDesc: 'فريق مستشاري لوكسفونو متاح على مدار الساعة للإجابة على استفساراتكم وتلبية رغباتكم.'
    },
    en: {
        siteName: 'LuxVono | Sovereign Luxury Marketplace',
        homeTitle: 'LuxVono | Sovereign Luxury Marketplace & Exclusive Collections',
        homeDesc: 'Discover exclusive luxury timepieces, high jewelry, designer handbags, royal fragrances, and bespoke concierge services with insured global delivery.',
        homeKeywords: 'LuxVono, luxury marketplace, fine watches, high jewelry, luxury shopping, royal perfumes, designer bags, GCC luxury, Saudi luxury, UAE luxury',
        videosTitle: 'Video Commerce & Luxury Theater | LuxVono',
        videosDesc: 'Experience interactive luxury video showcases and acquire rare items directly through the live video player.',
        vipTitle: 'VIP Private Client Lounge & Bespoke Services | LuxVono',
        vipDesc: 'Exclusive concierge and private acquisitions for elite collectors and discerning VIP clients worldwide.',
        liveTitle: 'Live Showcase & Sovereign Broadcasts | LuxVono',
        liveDesc: 'Join live luxury presentations and interact with world-renowned curators and luxury connoisseurs.',
        sourcingTitle: 'Bespoke Sourcing & Rare Acquisitions | LuxVono',
        sourcingDesc: 'Dedicated global luxury sourcing for rare timepieces, unique diamonds, and private-reserve items.',
        magazineTitle: 'The LuxVono Chronicle & Luxury Journal | LuxVono',
        magazineDesc: 'In-depth editorial coverage of haute horlogerie, high jewelry, luxury lifestyle, and private collections.',
        downloadTitle: 'Download the LuxVono Mobile App (Android & iOS) | LuxVono',
        downloadDesc: 'Download the official LuxVono app for seamless luxury shopping, live concierge, and exclusive alerts.',
        vendorTitle: 'Certified Luxury Vendor & Merchant Application | LuxVono',
        vendorDesc: 'Partner with LuxVono to showcase your certified luxury collections to prestigious clients globally.',
        contactTitle: 'Contact Private Concierge & VIP Support | LuxVono',
        contactDesc: 'Our private client advisors and concierge team are at your service 24/7.'
    },
    fr: {
        siteName: 'LuxVono | Marché du Luxe Souverain',
        homeTitle: 'LuxVono | Marché du Luxe Souverain et Collections Exclusives',
        homeDesc: 'Découvrez des montres d’exception, haute joaillerie, maroquinerie de prestige et parfums royaux avec livraison sécurisée mondiale.',
        homeKeywords: 'LuxVono, luxe, haute horlogerie, haute joaillerie, sacs de créateurs, conciergerie de luxe',
        videosTitle: 'Théâtre Vidéo & Commerce Interactif | LuxVono',
        videosDesc: 'Découvrez nos présentations vidéo exclusives et achetez directement vos pièces de collection.',
        vipTitle: 'Salon Privé VIP & Services Sur-Mesure | LuxVono',
        vipDesc: 'Services exclusifs et accompagnement personnalisé pour nos clients privés et collectionneurs.',
        liveTitle: 'Diffusions en Direct & Événements Privés | LuxVono',
        liveDesc: 'Participez à des présentations en direct de pièces rares avec nos experts.',
        sourcingTitle: 'Recherche Sur-Mesure & Pièces Rares | LuxVono',
        sourcingDesc: 'Service dédié pour l’acquisition de montres rares, bijoux uniques et pièces d’exception.',
        magazineTitle: 'La Chronique LuxVono | Magazine du Luxe',
        magazineDesc: 'Articles et tendances sur la haute horlogerie, joaillerie et l’art de vivre d’exception.',
        downloadTitle: 'Télécharger l’Application Mobile LuxVono | LuxVono',
        downloadDesc: 'Téléchargez l’application officielle LuxVono pour une expérience de luxe exclusive.',
        vendorTitle: 'Candidature Partenaire & Vendeur Certifié | LuxVono',
        vendorDesc: 'Rejoignez LuxVono et présentez vos créations de luxe à une clientèle internationale d’élite.',
        contactTitle: 'Contacter la Conciergerie Privée | LuxVono',
        contactDesc: 'Nos conseillers privés sont à votre écoute pour toute demande d’acquisition sur-mesure.'
    },
    de: {
        siteName: 'LuxVono | Souveräner Luxus-Marktplatz',
        homeTitle: 'LuxVono | Souveräner Luxus-Marktplatz & Exklusive Kollektionen',
        homeDesc: 'Entdecken Sie exklusive Luxusuhren, feinsten Schmuck, Designer-Handtaschen und königliche Düfte mit versichertem weltweiten Versand.',
        homeKeywords: 'LuxVono, Luxusmarktplatz, Luxusuhren, Haute Joaillerie, Designertaschen, VIP Concierge',
        videosTitle: 'Video-Theater & Interaktives Shopping | LuxVono',
        videosDesc: 'Erleben Sie exklusive Videopräsentationen und erwerben Sie Meisterwerke direkt.',
        vipTitle: 'VIP Private Client Lounge | LuxVono',
        vipDesc: 'Exklusive Betreuung und individuelle Akquisitionen für anspruchsvolle Sammler weltweit.',
        liveTitle: 'Live-Präsentationen & Stream | LuxVono',
        liveDesc: 'Erleben Sie Live-Vorführungen seltener Sammlerstücke mit unseren Experten.',
        sourcingTitle: 'Bespoke Sourcing & Seltene Akquisitionen | LuxVono',
        sourcingDesc: 'Globaler Beschaffungsservice für seltene Uhren, einzigartige Diamanten und private Kollektionen.',
        magazineTitle: 'Das LuxVono Magazin | The Chronicle',
        magazineDesc: 'Hintergründe und Berichte aus der Welt der Haute Horlogerie und des Luxus.',
        downloadTitle: 'LuxVono App Herunterladen (Android & iOS) | LuxVono',
        downloadDesc: 'Laden Sie die offizielle LuxVono App für mobiles Luxus-Shopping herunter.',
        vendorTitle: 'Händler- und Partner-Bewerbung | LuxVono',
        vendorDesc: 'Präsentieren Sie Ihre zertifizierten Luxusartikel einem anspruchsvollen internationalen Publikum.',
        contactTitle: 'Kontaktieren Sie Ihren Privaten Concierge | LuxVono',
        contactDesc: 'Unser VIP-Concierge steht Ihnen rund um die Uhr zur Verfügung.'
    },
    ru: {
        siteName: 'LuxVono | Маркетплейс Роскоши',
        homeTitle: 'LuxVono | Королевский маркетплейс роскоши и эксклюзивных коллекций',
        homeDesc: 'Эксклюзивные швейцарские часы, ювелирные шедевры, брендовые сумки и королевская парфюмерия с застрахованной доставкой по всему миру.',
        homeKeywords: 'LuxVono, маркетплейс роскоши, элитные часы, ювелирные изделия, брендовые сумки, VIP консьерж',
        videosTitle: 'Видео-витрина и интерактивные покупки | LuxVono',
        videosDesc: 'Смотрите эксклюзивные видеообзоры и приобретайте редкие шедевры напрямую.',
        vipTitle: 'VIP Лаунж для частных клиентов | LuxVono',
        vipDesc: 'Персональное обслуживание и закрытые приобретения для самых взыскательных клиентов.',
        liveTitle: 'Прямые трансляции и закрытые показы | LuxVono',
        liveDesc: 'Присоединяйтесь к прямым эфирам с экспертами высокого часового и ювелирного искусства.',
        sourcingTitle: 'Индивидуальный поиск редких предметов | LuxVono',
        sourcingDesc: 'Глобальный поиск редких часов, уникальных бриллиантов и раритетов по всему миру.',
        magazineTitle: 'Журнал LuxVono Chronicle | Мир Роскоши',
        magazineDesc: 'Эксклюзивные статьи о высоком часовом искусстве, ювелирных украшениях и стиле.',
        downloadTitle: 'Скачать приложение LuxVono (Android и iOS) | LuxVono',
        downloadDesc: 'Установите официальное приложение LuxVono для быстрого доступа к покупкам.',
        vendorTitle: 'Регистрация сертифицированного продавца | LuxVono',
        vendorDesc: 'Станьте партнером LuxVono и представьте свои роскошные коллекции элитной аудитории.',
        contactTitle: 'Связь с консьерж-сервисом LuxVono | LuxVono',
        contactDesc: 'Наши частные консультанты на связи 24/7 для выполнения любых эксклюзивных заказов.'
    },
    tr: {
        siteName: 'LuxVono | Lüks Alışveriş Pazarı',
        homeTitle: 'LuxVono | Lüks Alışveriş ve Özel Koleksiyonlar Pazarı',
        homeDesc: 'Seçkin lüks saatler, yüksek mücevherler, tasarım çantalar ve kraliyet parfümlerini sigortalı küresel teslimatla keşfedin.',
        homeKeywords: 'LuxVono, lüks alışveriş, lüks saatler, mücevherat, tasarım çantalar, VIP konsiyerj',
        videosTitle: 'Video Tiyatro ve Alışveriş | LuxVono',
        videosDesc: 'Özel video sunumlarını izleyin ve nadir parçaları doğrudan edinin.',
        vipTitle: 'VIP Özel Müşteri Salonu | LuxVono',
        vipDesc: 'Dünya çapındaki seçkin koleksiyonerler için ayrıcalıklı özel danışmanlık hizmetleri.',
        liveTitle: 'Canlı Yayınlar ve Sunumlar | LuxVono',
        liveDesc: 'Nadir lüks parçaların uzmanlar eşliğinde canlı sunumlarına katılın.',
        sourcingTitle: 'Özel Tedarik ve Nadir Parça Temini | LuxVono',
        sourcingDesc: 'Nadir saatler, eşsiz elmaslar ve özel koleksiyon parçaları için küresel tedarik hizmeti.',
        magazineTitle: 'LuxVono Chronicle Dergisi | Lüks Dünyası',
        magazineDesc: 'Lüks yaşam, saat ustalığı ve mücevher dünyasından en son haberler.',
        downloadTitle: 'LuxVono Mobil Uygulamasını İndirin | LuxVono',
        downloadDesc: 'Kusursuz bir lüks alışveriş deneyimi için LuxVono mobil uygulamasını indirin.',
        vendorTitle: 'Sertifikalı Satıcı Başvurusu | LuxVono',
        vendorDesc: 'Lüks koleksiyonlarınızı dünya çapında seçkin müşterilere sunmak için LuxVono ortağı olun.',
        contactTitle: 'Özel Konsiyerj ile İletişime Geçin | LuxVono',
        contactDesc: 'Özel müşteri danışmanlarımız 7/24 hizmetinizdedir.'
    },
    es: {
        siteName: 'LuxVono | Mercado de Lujo Soberano',
        homeTitle: 'LuxVono | Mercado de Lujo Soberano y Colecciones Exclusivas',
        homeDesc: 'Descubra relojes de alta gama, alta joyería, bolsos de diseñador y fragancias reales con entrega global asegurada.',
        homeKeywords: 'LuxVono, mercado de lujo, alta relojería, alta joyería, bolsos de diseñador, compras de lujo, VIP concerge',
        videosTitle: 'Teatro de Vídeo y Comercio Interactivo | LuxVono',
        videosDesc: 'Descubra presentaciones exclusivas en vídeo y adquiera piezas maestras directamente a través del reproductor en vivo.',
        vipTitle: 'Salón VIP para Clientes Privados | LuxVono',
        vipDesc: 'Servicios exclusivos de conserjería y adquisiciones privadas para clientes selectos y coleccionistas en todo el mundo.',
        liveTitle: 'Transmisiones en Vivo y Presentaciones Exclusivas | LuxVono',
        liveDesc: 'Únase a transmisiones en directo de piezas de lujo exclusivas e interactúe con expertos y curadores mundiales.',
        sourcingTitle: 'Búsqueda Personalizada y Piezas Exclusivas | LuxVono',
        sourcingDesc: 'Servicio global dedicado a la adquisición de relojes raros, diamantes únicos y piezas privadas.',
        magazineTitle: 'La Crónica LuxVono | Revista de Lujo',
        magazineDesc: 'Artículos exclusivos sobre alta relojería, joyería fina y el arte de vivir con elegancia.',
        downloadTitle: 'Descargar la Aplicación Móvil LuxVono (Android e iOS) | LuxVono',
        downloadDesc: 'Descargue la aplicación oficial de LuxVono para una experiencia de compras de lujo inigualable.',
        vendorTitle: 'Solicitud de Vendedor y Comercio Certificado | LuxVono',
        vendorDesc: 'Asóciese con LuxVono para exhibir sus creaciones de lujo a una clientela internacional de élite.',
        contactTitle: 'Contactar con la Conserjería Privada | LuxVono',
        contactDesc: 'Nuestros asesores privados y equipo de conserjería están a su disposición las 24 horas del día, los 7 días de la semana.'
    },
    it: {
        siteName: 'LuxVono | Mercato del Lusso Sovrano',
        homeTitle: 'LuxVono | Mercato del Lusso Sovrano e Collezioni Esclusive',
        homeDesc: 'Scopri orologi esclusivi, alta gioielleria, borse firmate e profumi regali con consegna assicurata in tutto il mondo.',
        homeKeywords: 'LuxVono, mercato del lusso, alta orologeria, alta gioielleria, borse firmate, shopping di lusso',
        videosTitle: 'Teatro Video & Shopping Interattivo | LuxVono',
        videosDesc: 'Scopri le presentazioni video esclusive e acquista capolavori rari direttamente dal player video dal vivo.',
        vipTitle: 'VIP Private Client Lounge & Servizi Esclusivi | LuxVono',
        vipDesc: 'Servizi di concierge personalizzati e acquisizioni private per collezionisti e clienti d’élite mondiali.',
        liveTitle: 'Dirette dal Vivo & Vetrina Sovrana | LuxVono',
        liveDesc: 'Partecipa a presentazioni dal vivo di creazioni di lusso e interagisci con curatori e maestri del settore.',
        sourcingTitle: 'Ricerca Su Misura & Acquisizioni Rare | LuxVono',
        sourcingDesc: 'Servizio globale di ricerca e approvvigionamento per orologi rari, diamanti unici e pezzi da collezione.',
        magazineTitle: 'The LuxVono Chronicle | Rivista del Lusso',
        magazineDesc: 'Approfondimenti esclusivi su alta orologeria, alta gioielleria, lifestyle e collezioni private.',
        downloadTitle: 'Scarica l’App Mobile LuxVono (Android e iOS) | LuxVono',
        downloadDesc: 'Scarica l’app ufficiale LuxVono per un’esperienza regale con notifiche su collezioni esclusive.',
        vendorTitle: 'Candidatura Fornitore e Commerciante Certificato | LuxVono',
        vendorDesc: 'Collabora con LuxVono per presentare le tue prestigiose collezioni a una clientela globale selezionata.',
        contactTitle: 'Contatta il Concierge Privato VIP | LuxVono',
        contactDesc: 'I nostri consulenti privati e il team di concierge sono al tuo servizio 24 ore su 24, 7 giorni su 7.'
    },
    zh: {
        siteName: 'LuxVono | 皇家至尊奢华典藏平台',
        homeTitle: 'LuxVono | 皇家至尊奢华购物平台与传世珍藏 | LuxVono',
        homeDesc: '探索独家瑞士名表、高级珠宝、名师手袋、皇家香氛与高定风尚，享VIP私人管家定制服务与全球保价极速配送。',
        homeKeywords: 'LuxVono, 奢华购物, 瑞士名表, 高级珠宝, 皇家香氛, 品牌手袋, 顶级奢侈品, VIP私人顾问',
        videosTitle: '沉浸式名品剧场与互动购物 | LuxVono',
        videosDesc: '尽享互动式奢品视频鉴赏，通过实时播放器一键典藏传世珍品。',
        vipTitle: 'VIP贵宾私人沙龙与尊崇定制 | LuxVono',
        vipDesc: '为全球尊贵藏家与精英客户提供专属私人定制顾问及全球珍稀瑰宝代寻服务。',
        liveTitle: '实况品鉴直播与皇家臻品展示 | LuxVono',
        liveDesc: '加入名师实况直播鉴赏专场，与国际奢侈品专家实时互动交流。',
        sourcingTitle: '全球代寻臻品与传世珍藏定制 | LuxVono',
        sourcingDesc: '专为名流定制的全球稀世腕表、极品彩钻与私藏古董专业寻购通道。',
        magazineTitle: 'LuxVono 皇家纪事与典雅名流志 | The Chronicle',
        magazineDesc: '深度品评顶级制表工艺、传世高级珠宝、贵族名仕生活与私藏典籍。',
        downloadTitle: '下载 LuxVono 手机应用 (Android & iOS) | LuxVono',
        downloadDesc: '立即下载官方 LuxVono App，畅享皇家至尊购物体验与专属折扣提醒。',
        vendorTitle: '认证奢华品牌与尊崇商家入驻申请 | LuxVono',
        vendorDesc: '入驻 LuxVono 全球官方合作商网络，面向全球尊荣精英客户呈现您的匠心杰作。',
        contactTitle: '联络私人管家与VIP尊享服务 | LuxVono',
        contactDesc: 'LuxVono 私人定制顾问及尊崇管家团队全天候 24/7 恭候竭诚为您服务。'
    },
    ja: {
        siteName: 'LuxVono | 至高のロイヤル・ラグジュアリー・マーケット',
        homeTitle: 'LuxVono | 至高のロイヤル・ラグジュアリー・マーケット＆限定コレクション',
        homeDesc: '厳選された高級スイス時計、ハイジュエリー、デザイナーズバッグ、王室御用達フレグランスを安心の全額保険付き国際配送でお届けします。',
        homeKeywords: 'LuxVono, ラグジュアリーマーケット, 高級時計, ハイジュエリー, ブランドバッグ, VIPコンシェルジュ',
        videosTitle: 'ビデオシアター＆インタラクティブ・ショッピング | LuxVono',
        videosDesc: '独占映像で名品の細部を鑑賞し、ライブビデオプレイヤーから直接ご購入いただけます。',
        vipTitle: 'VIPプライベート・クライアント・ラウンジ | LuxVono',
        vipDesc: '世界中の洗練されたコレクターのための専属コンシェルジュと特別購入サポート。',
        liveTitle: 'ライブショーケース＆ロイヤルブロードキャスト | LuxVono',
        liveDesc: '専門家による限定逸品のリアルタイム・ライブ配信に参加し、対話しながらご購入いただけます。',
        sourcingTitle: 'オーダーメイド・ソーシング＆希少品調達 | LuxVono',
        sourcingDesc: '世界中の希少な時計、比類なきダイヤモンド、特別収蔵品の専属調達サービス。',
        magazineTitle: 'LuxVono クロニクル・ラグジュアリージャーナル | The Chronicle',
        magazineDesc: 'オート・オルロジュリー、ハイジュエリー、洗練されたライフスタイルの深層記事。',
        downloadTitle: 'LuxVono 公式モバイルアプリをダウンロード (Android & iOS) | LuxVono',
        downloadDesc: '極上のショッピング体験と限定オファー通知をお届けする公式アプリをダウンロード。',
        vendorTitle: '公認パートナー・出店申請 | LuxVono',
        vendorDesc: 'LuxVonoと提携し、世界中のエリート層に向けて至高のコレクションを展示。',
        contactTitle: 'プライベート・コンシェルジュへのお問い合わせ | LuxVono',
        contactDesc: 'VIP専属アドバイザーとコンシェルジュチームが24時間365日対応いたします。'
    },
    ko: {
        siteName: 'LuxVono | 소버린 로열 럭셔리 마켓플레이스',
        homeTitle: 'LuxVono | 최고급 로열 럭셔리 마켓플레이스 & 익스클루시브 컬렉션',
        homeDesc: '독보적인 하이엔드 스위스 시계, 하이 주얼리, 명품 핸드백, 로열 향수를 전액 보험 국제 배송으로 안전하게 만나보세요.',
        homeKeywords: 'LuxVono, 럭셔리 쇼핑, 명품 시계, 하이 주얼리, 로열 향수, 디자이너 백, VIP 컨시어지',
        videosTitle: '비디오 시어터 & 인터랙티브 쇼핑 | LuxVono',
        videosDesc: '독점 비디오 쇼케이스를 통해 예술적 걸작을 감상하고 실시간으로 직접 소장하세요.',
        vipTitle: 'VIP 프라이빗 클라이언트 라운지 | LuxVono',
        vipDesc: '전 세계 최상위 컬렉터를 위한 전담 맞춤형 컨시어지 및 프라이빗 옥션 서비스.',
        liveTitle: '라이브 쇼케이스 & 로열 브로드캐스트 | LuxVono',
        liveDesc: '럭셔리 큐레이터와 함께하는 실시간 라이브 방송에 참여하여 희귀 걸작을 만나보세요.',
        sourcingTitle: '비스포크 소싱 & 희귀 에디션 조달 | LuxVono',
        sourcingDesc: '희귀 명품 시계, 독보적인 다이아몬드 및 개인 소장품을 위한 글로벌 맞춤 소싱 서비스.',
        magazineTitle: 'LuxVono 크로니클 저널 | The Chronicle',
        magazineDesc: '오트 오를로제리, 하이 주얼리, 럭셔리 라이프스타일에 대한 심층 보도.',
        downloadTitle: 'LuxVono 공식 모바일 앱 다운로드 (Android & iOS) | LuxVono',
        downloadDesc: '원활한 럭셔리 쇼핑과 특별 알림을 제공하는 공식 모바일 앱을 지금 다운로드하세요.',
        vendorTitle: '공식 파트너사 및 판매자 신청 | LuxVono',
        vendorDesc: 'LuxVono의 글로벌 엘리트 고객층에게 귀사의 최고급 컬렉션을 선보이세요.',
        contactTitle: '프라이빗 컨시어지 상담 문의 | LuxVono',
        contactDesc: '프라이빗 클라이언트 전문 어드바이저 팀이 연중무휴 24시간 대기하고 있습니다.'
    },
    so: {
        siteName: 'LuxVono | Suuqa Boqortooyada ee Alaabaha Qaliga ah',
        homeTitle: 'LuxVono | Suuqa Boqortooyada ee Alaabaha Qaliga ah & Ururinta Gaarka ah',
        homeDesc: 'Ka hel saacadaha ugu qaalisan Switzerland, dahabka sarreeya, boorsooyinka caanka ah, iyo barafuunnada boqortooyada oo lagu geeyo damaanad buuxda adduunka oo dhan.',
        homeKeywords: 'LuxVono, suuq qaali ah, saacado qaali ah, dahab, barafuun boqortooyo, dukaameysi qaali ah',
        videosTitle: 'Masraxa Fiidiyowga & Dukaameysiga Tooska ah | LuxVono',
        videosDesc: 'Daawo fiidiyowyo gaar ah oo ku saabsan alaabta qaaliga ah oo si toos ah uga iibso ciyaartoyga tooska ah.',
        vipTitle: 'Qolka VIP-da Gaarka ah & Adeegyada Khaaska ah | LuxVono',
        vipDesc: 'Adeegyo gaar ah oo loogu talagalay dadka caanka ah iyo ururiyeyaasha caalamiga ah.',
        liveTitle: 'Bandhiga Tooska ah & Baahinta Boqortooyada | LuxVono',
        liveDesc: 'Ku soo biir baahinta tooska ah ee alaabta qaaliga ah oo la falgal khubarada ugu sarreysa.',
        sourcingTitle: 'Raadinta Gaarka ah ee Alaabaha Dhifka ah | LuxVono',
        sourcingDesc: 'Adeeg caalami ah oo lagu raadiyo saacadaha dhifka ah, dheemanka qaaliga ah, iyo alaabta gaarka ah.',
        magazineTitle: 'Joornaalka LuxVono Chronicle | The Chronicle',
        magazineDesc: 'Maqaallo qoto dheer oo ku saabsan saacadaha qaaliga ah, dahabka sarreeya, iyo qaab nololeedka qaaliga ah.',
        downloadTitle: 'Soo Dejiso App-ka Moobaylka ee LuxVono | LuxVono',
        downloadDesc: 'Soo dejiso app-ka rasmiga ah ee LuxVono si aad u hesho waayo-aragnimo dukaameysi boqortooyo oo degdeg ah.',
        vendorTitle: 'Codsiga Ganacsadaha La Aqoonsan yahay | LuxVono',
        vendorDesc: 'Ku biir LuxVono si aad ugu soo bandhigto alaabtaada qaaliga ah macaamiisha caalamiga ah ee heerka sare ah.',
        contactTitle: 'La Xiriir Caawiyaha Gaarka ah ee VIP | LuxVono',
        contactDesc: 'Kooxda lataliyeyaasha gaarka ah ee LuxVono waxay diyaar u yihiin 24/7 si ay kuugu adeegaan.'
    },
    am: {
        siteName: 'LuxVono | የንጉሣዊ ቅንጦት ግብይት መድረክ',
        homeTitle: 'LuxVono | የንጉሣዊ ቅንጦት የገበያ መድረክ እና ብቸኛ ስብስቦች | LuxVono',
        homeDesc: 'የስዊስ ሰዓቶችን፣ የከበሩ ጌጣጌጦችን፣ የዲዛይነር ቦርሳዎችን እና የንጉሣዊ ሽቶዎችን ከአስተማማኝ ዓለም አቀፍ ማድረስ ጋር ይግዙ።',
        homeKeywords: 'LuxVono, ቅንጦት ግብይት, የቅንጦት ሰዓቶች, ጌጣጌጦች, ንጉሣዊ ሽቶዎች, VIP አገልግሎት',
        videosTitle: 'የቪዲዮ ቲያትር እና የቀጥታ ግብይት | LuxVono',
        videosDesc: 'ልዩ የቪዲዮ ትዕይንቶችን ይመልከቱ እና ብርቅዬ እቃዎችን በቀጥታ ከቪዲዮ ማጫወቻው ይግዙ።',
        vipTitle: 'የVIP ደንበኞች ላውንጅ እና ልዩ አገልግሎት | LuxVono',
        vipDesc: 'ለከፍተኛ ሰብሳቢዎች እና ክቡራን ደንበኞች የተዘጋጀ ብቸኛ የግል አማካሪ አገልግሎት።',
        liveTitle: 'የቀጥታ ስርጭት እና ንጉሣዊ ማሳያ | LuxVono',
        liveDesc: 'ልዩ የቀጥታ የቅንጦት ስርጭቶችን ይቀላቀሉ እና ከዓለም አቀፍ ባለሙያዎች ጋር ይገናኙ።',
        sourcingTitle: 'ብርቅዬ እቃዎችን የማፈላለግ ልዩ አገልግሎት | LuxVono',
        sourcingDesc: 'ብርቅዬ ሰዓቶችን፣ ልዩ አልማዞችን እና ውድ ቅርሶችን በዓለም ዙሪያ የማፈላለግ አገልግሎት።',
        magazineTitle: 'የLuxVono ዜና መዋዕል መጽሔት | The Chronicle',
        magazineDesc: 'ስለ ከፍተኛ ሰዓት ስራ፣ ጌጣጌጥ እና የቅንጦት አኗኗር ጥልቅ መጣጥፎች።',
        downloadTitle: 'የLuxVono የሞባይል መተግበሪያን ያውርዱ | LuxVono',
        downloadDesc: 'ለፈጣን ንጉሣዊ ግብይት እና ለየት ያሉ ማሳወቂያዎች ይፋዊውን የLuxVono መተግበሪያ ያውርዱ።',
        vendorTitle: 'የተረጋገጠ ነጋዴ ምዝገባ | LuxVono',
        vendorDesc: 'የእርስዎን የቅንጦት ስብስቦች ለዓለም አቀፍ የላቀ ደንበኞች ለማቅረብ ከLuxVono ጋር ይተባበሩ።',
        contactTitle: 'የግል ረዳትና VIP አማካሪን ያነጋግሩ | LuxVono',
        contactDesc: 'የLuxVono የግል አማካሪዎች ጥያቄዎትን ለመመለስ 24/7 ዝግጁ ናቸው።'
    },
    gdq: {
        siteName: 'لوكسفونو | المنصة الملكية للتسوق الفاخر باللسان المهري',
        homeTitle: 'لوكسفونو | المنصة الملكية للمقتنيات النادرة والقطع الفاخرة | LuxVono',
        homeDesc: 'تسوق أندر الساعات الفاخرة، المجوهرات الملكية، العطور الخالصة، والحقائب الحصرية مع توصيل موثوق ومضمون لكافة بلدان الخليج والعالم.',
        homeKeywords: 'لوكسفونو, اللسان المهري, تسوق فاخر, ساعات ملكية, مجوهرات, عطور, مقتنيات نادرة',
        videosTitle: 'مسرح البث وعروض الفيديو التفاعلية | لوكسفونو',
        videosDesc: 'شاهد العروض المرئية المباشرة للمقتنيات الفارهة واقتنِ مشترياتك مباشرة من الفيديو.',
        vipTitle: 'صالة كبار الشخصيات VIP والخدمات الملكية | لوكسفونو',
        vipDesc: 'خدمات مخصصة لأعيان المقتنين مع مستشار خاص لتلبية أرقى الطلبات الخاصة.',
        liveTitle: 'البث الحي المباشر ومسرح العروض الحصرية | لوكسفونو',
        liveDesc: 'انضم إلى جلسات البث الحي المباشر وتفاعل مع كبار خبراء المقتنيات النادرة.',
        sourcingTitle: 'البحث عن القطع النادرة وتأمين المقتنيات | لوكسفونو',
        sourcingDesc: 'خدمة تأمين أندر الساعات والمجوهرات المخصصة من كافة أنحاء العالم.',
        magazineTitle: 'مجلة لوكسفونو الفاخرة وأخبار المقتنيات | The Chronicle',
        magazineDesc: 'مقالات وأخبار حصرية عن عالم الساعات الفاخرة والمجوهرات والتراث الراقي.',
        downloadTitle: 'تحميل تطبيق لوكسفونو للهواتف الذكية | LuxVono',
        downloadDesc: 'حمّل تطبيق لوكسفونو واستمتع بتجربة تسوق ملكية سريعة وخاصة.',
        vendorTitle: 'تسجيل مورد معتمد وتاجر فاخر | لوكسفونو',
        vendorDesc: 'انضم إلى شبكة لوكسفونو واعرض منتجاتك الفاخرة لصفوة العملاء حول العالم.',
        contactTitle: 'تواصل مع الكونسيرج والمساعد الخاص | لوكسفونو',
        contactDesc: 'فريق مستشاري لوكسفونو متاح دائماً على مدار الساعة لتلبية كافة طلباتكم.'
    }
};

const HOME_BREADCRUMB_NAMES = {
    ar: 'الرئيسية',
    en: 'Home',
    fr: 'Accueil',
    de: 'Startseite',
    ru: 'Главная',
    tr: 'Ana Sayfa',
    es: 'Inicio',
    it: 'Home',
    zh: '首页',
    ja: 'ホーム',
    ko: '홈',
    so: 'Hoyga',
    am: 'መነሻ',
    gdq: 'الرئيسية'
};

const CATEGORY_SEO_TEMPLATES = {
    ar: (cat) => ({ title: `مجموعات ${cat} الفاخرة | لوكسفونو`, desc: `استكشف أرقى وأفخم مجموعات ${cat} الحصرية لدى لوكسفونو مع شهادات الأصالة المعتمدة.` }),
    en: (cat) => ({ title: `Exclusive ${cat} Collections | LuxVono`, desc: `Explore certified high-end ${cat} luxury collections at LuxVono with insured global delivery.` }),
    fr: (cat) => ({ title: `Collections Exclusives de ${cat} | LuxVono`, desc: `Découvrez les prestigieuses collections de ${cat} chez LuxVono avec certificat d'authenticité.` }),
    de: (cat) => ({ title: `Exklusive ${cat} Kollektionen | LuxVono`, desc: `Entdecken Sie zertifizierte ${cat} Luxus-Kollektionen bei LuxVono mit versichertem Versand.` }),
    ru: (cat) => ({ title: `Эксклюзивные коллекции ${cat} | LuxVono`, desc: `Откройте для себя сертифицированные коллекции ${cat} на LuxVono с гарантией подлинности.` }),
    tr: (cat) => ({ title: `Özel ${cat} Koleksiyonları | LuxVono`, desc: `Sertifikalı lüks ${cat} koleksiyonlarını LuxVono güvencesi ve sigortalı teslimatla keşfedin.` }),
    es: (cat) => ({ title: `Colecciones Exclusivas de ${cat} | LuxVono`, desc: `Explore colecciones de lujo certificadas de ${cat} en LuxVono con envío asegurado.` }),
    it: (cat) => ({ title: `Collezioni Esclusive di ${cat} | LuxVono`, desc: `Scopri le prestigiose collezioni ${cat} su LuxVono con certificato di autenticità.` }),
    zh: (cat) => ({ title: `尊享 ${cat} 传世典藏系列 | LuxVono`, desc: `探索 LuxVono 官方认证 ${cat} 顶级奢华典藏系列，享保价极速配送与正品保证。` }),
    ja: (cat) => ({ title: `限定 ${cat} コレクション | LuxVono`, desc: `LuxVonoが厳選した正規認定 ${cat} ラグジュアリーコレクションをご覧ください。` }),
    ko: (cat) => ({ title: `익스클루시브 ${cat} 컬렉션 | LuxVono`, desc: `LuxVono에서 정품 인증된 최고급 ${cat} 럭셔리 컬렉션을 안전한 배송으로 만나보세요.` }),
    so: (cat) => ({ title: `Ururinta Gaarka ah ee ${cat} | LuxVono`, desc: `Ka hel ururinta qaaliga ah ee ${cat} ee LuxVono oo leh shahaado rasmi ah.` }),
    am: (cat) => ({ title: `ልዩ የ${cat} ስብስቦች | LuxVono`, desc: `የተረጋገጡ የ${cat} የቅንጦት ስብስቦችን በLuxVono ከአስተማማኝ ማድረስ ጋር ያስሱ።` }),
    gdq: (cat) => ({ title: `مجموعات ${cat} الفاخرة | لوكسفونو`, desc: `استكشف أرقى وأفخم مجموعات ${cat} الحصرية لدى لوكسفونو باللسان المهري.` })
};

const PRODUCT_SEO_TEMPLATES = {
    ar: { title: 'تفاصيل المقتنى الفاخر | لوكسفونو', desc: 'مقتنى فاخر أصلي مع ضمان شامل وخدمة المساعد الشخصي VIP من لوكسفونو.' },
    en: { title: 'Luxury Piece Showcase | LuxVono', desc: 'Authentic certified luxury item with bespoke concierge service and guaranteed provenance at LuxVono.' },
    fr: { title: 'Pièce d’Exception | LuxVono', desc: 'Pièce de luxe authentique et certifiée avec service de conciergerie privée chez LuxVono.' },
    de: { title: 'Exklusives Meisterwerk | LuxVono', desc: 'Zertifiziertes Original-Luxusstück mit VIP-Concierge-Service und Echtheitsgarantie bei LuxVono.' },
    ru: { title: 'Шедевр Высокого Искусства | LuxVono', desc: 'Сертифицированный предмет роскоши с персональным консьерж-сервисом LuxVono.' },
    tr: { title: 'Lüks Eser Detayı | LuxVono', desc: 'LuxVono VIP konsiyerj hizmeti ve tam orijinallik garantisi ile sertifikalı lüks parça.' },
    es: { title: 'Pieza de Alta Gama | LuxVono', desc: 'Pieza de lujo auténtica certificada con servicio de conserjería VIP en LuxVono.' },
    it: { title: 'Capolavoro di Lusso | LuxVono', desc: 'Pezzo di lusso autentico e certificato con servizio di concierge privato su LuxVono.' },
    zh: { title: '传世珍品品鉴 | LuxVono', desc: 'LuxVono 官方正品认证奢华瑰宝，享专属私人管家咨询及终身保真保证。' },
    ja: { title: '至高の名品詳細 | LuxVono', desc: '専属VIPコンシェルジュサービスと真正性保証が付いた正規認定ラグジュアリーピース。' },
    ko: { title: '하이엔드 명품 상세 | LuxVono', desc: '전담 VIP 컨시어지 서비스와 정품 보증이 제공되는 공인 럭셔리 마스터피스.' },
    so: { title: 'Faahfaahinta Alaabta Qaliga ah | LuxVono', desc: 'Alaab qaali ah oo asal ah oo leh damaanad buuxda iyo adeegga VIP ee LuxVono.' },
    am: { title: 'የቅንጦት እቃ ዝርዝር | LuxVono', desc: 'የተረጋገጠ ኦሪጅናል የቅንጦት እቃ ከሙሉ ዋስትና እና ከLuxVono VIP አገልግሎት ጋር።' },
    gdq: { title: 'تفاصيل المقتنى الفاخر باللسان المهري | لوكسفونو', desc: 'مقتنى فاخر أصلي مع ضمان شامل وخدمة المساعد الشخصي VIP من لوكسفونو.' }
};

const setOrCreateMeta = (name, content, isProperty = false) => {
    if (!content) return;
    const attribute = isProperty ? 'property' : 'name';
    let element = document.querySelector(`meta[${attribute}="${name}"]`);
    if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
    }
    element.setAttribute('content', content);
};

const setOrCreateLink = (rel, href) => {
    if (!href) return;
    let element = document.querySelector(`link[rel="${rel}"]`);
    if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
    }
    element.setAttribute('href', href);
};

export default function SeoEngine() {
    const location = useLocation();
    const { country: marketCountry, language: marketLanguage } = useMarket();

    useEffect(() => {
        const lang = (marketLanguage || resolveCurrentLang() || 'ar').toLowerCase();
        const country = (marketCountry || resolveCurrentCountry() || 'SA').toUpperCase();
        const rtl = isRTL(lang);

        // Update html lang and dir
        document.documentElement.lang = lang;
        document.documentElement.dir = rtl ? 'rtl' : 'ltr';

        const dict = SEO_DICTIONARY[lang] || SEO_DICTIONARY['en'] || SEO_DICTIONARY['ar'];
        const pathname = location.pathname.toLowerCase();

        let pageTitle = dict.homeTitle;
        let pageDesc = dict.homeDesc;
        let pageKeywords = dict.homeKeywords;

        if (pathname.includes('/videos')) {
            pageTitle = dict.videosTitle;
            pageDesc = dict.videosDesc;
        } else if (pathname.includes('/vip')) {
            pageTitle = dict.vipTitle;
            pageDesc = dict.vipDesc;
        } else if (pathname.includes('/live')) {
            pageTitle = dict.liveTitle;
            pageDesc = dict.liveDesc;
        } else if (pathname.includes('/sourcing')) {
            pageTitle = dict.sourcingTitle;
            pageDesc = dict.sourcingDesc;
        } else if (pathname.includes('/magazine')) {
            pageTitle = dict.magazineTitle;
            pageDesc = dict.magazineDesc;
        } else if (pathname.includes('/download')) {
            pageTitle = dict.downloadTitle;
            pageDesc = dict.downloadDesc;
        } else if (pathname.includes('/vendor')) {
            pageTitle = dict.vendorTitle;
            pageDesc = dict.vendorDesc;
        } else if (pathname.includes('/contact')) {
            pageTitle = dict.contactTitle;
            pageDesc = dict.contactDesc;
        } else if (pathname.includes('/category/')) {
            const rawCat = pathname.split('/category/')[1]?.replace(/[-_]/g, ' ')?.toUpperCase() || '';
            const catGenerator = CATEGORY_SEO_TEMPLATES[lang] || CATEGORY_SEO_TEMPLATES['en'] || CATEGORY_SEO_TEMPLATES['ar'];
            const catInfo = catGenerator(rawCat);
            pageTitle = catInfo.title;
            pageDesc = catInfo.desc;
        } else if (pathname.includes('/product/')) {
            const prodInfo = PRODUCT_SEO_TEMPLATES[lang] || PRODUCT_SEO_TEMPLATES['en'] || PRODUCT_SEO_TEMPLATES['ar'];
            pageTitle = prodInfo.title;
            pageDesc = prodInfo.desc;
        }

        // Apply Title
        document.title = pageTitle;

        // Apply Meta Tags
        setOrCreateMeta('description', pageDesc);
        setOrCreateMeta('keywords', pageKeywords);
        setOrCreateMeta('og:title', pageTitle, true);
        setOrCreateMeta('og:description', pageDesc, true);
        setOrCreateMeta('og:site_name', dict.siteName, true);
        setOrCreateMeta('og:url', `https://luxvono.com${location.pathname}`, true);
        setOrCreateMeta('og:locale', lang === 'ar' ? 'ar_SA' : `${lang}_${country}`, true);

        setOrCreateMeta('twitter:title', pageTitle);
        setOrCreateMeta('twitter:description', pageDesc);

        // Canonical Link
        const canonicalUrl = location.pathname === '/' || location.pathname === ''
            ? 'https://luxvono.com/'
            : `https://luxvono.com${location.pathname.endsWith('/') ? location.pathname : location.pathname + '/'}`;
        setOrCreateLink('canonical', canonicalUrl);

        // Dynamic International Hreflang Tags (SHEIN-style for all 14 languages)
        try {
            document.querySelectorAll('link[data-luxvono-hreflang="true"]').forEach(el => el.remove());

            const subpath = location.pathname.replace(/^\/[a-zA-Z]{2}\/[a-zA-Z]{2,3}(?:-[a-zA-Z]{2})?/, '') || '';
            const cleanSub = subpath.startsWith('/') ? subpath : (subpath ? `/${subpath}` : '/');

            const hreflangs = [
                { hreflang: 'x-default', href: 'https://luxvono.com/' },
                { hreflang: 'ar-SA', href: `https://luxvono.com/sa/ar${cleanSub}` },
                { hreflang: 'en-SA', href: `https://luxvono.com/sa/en${cleanSub}` },
                { hreflang: 'ar-AE', href: `https://luxvono.com/ae/ar${cleanSub}` },
                { hreflang: 'en-AE', href: `https://luxvono.com/ae/en${cleanSub}` },
                { hreflang: 'en-US', href: `https://luxvono.com/us/en${cleanSub}` },
                { hreflang: 'es-US', href: `https://luxvono.com/us/es${cleanSub}` },
                { hreflang: 'en-GB', href: `https://luxvono.com/gb/en${cleanSub}` },
                { hreflang: 'de-DE', href: `https://luxvono.com/de/de${cleanSub}` },
                { hreflang: 'en-DE', href: `https://luxvono.com/de/en${cleanSub}` },
                { hreflang: 'fr-FR', href: `https://luxvono.com/fr/fr${cleanSub}` },
                { hreflang: 'es-ES', href: `https://luxvono.com/es/es${cleanSub}` },
                { hreflang: 'it-IT', href: `https://luxvono.com/it/it${cleanSub}` },
                { hreflang: 'ru-RU', href: `https://luxvono.com/ru/ru${cleanSub}` },
                { hreflang: 'tr-TR', href: `https://luxvono.com/tr/tr${cleanSub}` },
                { hreflang: 'zh-CN', href: `https://luxvono.com/cn/zh${cleanSub}` },
                { hreflang: 'ja-JP', href: `https://luxvono.com/jp/ja${cleanSub}` },
                { hreflang: 'ko-KR', href: `https://luxvono.com/kr/ko${cleanSub}` },
                { hreflang: 'so-SO', href: `https://luxvono.com/so/so${cleanSub}` },
                { hreflang: 'am-ET', href: `https://luxvono.com/et/am${cleanSub}` },
                { hreflang: 'ar-KW', href: `https://luxvono.com/kw/ar${cleanSub}` },
                { hreflang: 'ar-QA', href: `https://luxvono.com/qa/ar${cleanSub}` },
                { hreflang: 'ar-OM', href: `https://luxvono.com/om/ar${cleanSub}` },
                { hreflang: 'ar-BH', href: `https://luxvono.com/bh/ar${cleanSub}` }
            ];

            hreflangs.forEach(({ hreflang, href }) => {
                const link = document.createElement('link');
                link.setAttribute('rel', 'alternate');
                link.setAttribute('hreflang', hreflang);
                link.setAttribute('href', href);
                link.setAttribute('data-luxvono-hreflang', 'true');
                document.head.appendChild(link);
            });
        } catch (_) {}

        // Dynamic Breadcrumb Schema
        let breadcrumbJson = document.getElementById('lux-breadcrumb-schema');
        if (!breadcrumbJson) {
            breadcrumbJson = document.createElement('script');
            breadcrumbJson.id = 'lux-breadcrumb-schema';
            breadcrumbJson.type = 'application/ld+json';
            document.head.appendChild(breadcrumbJson);
        }

        const homeName = HOME_BREADCRUMB_NAMES[lang] || (rtl ? "الرئيسية" : "Home");
        const breadcrumbs = [
            { "@type": "ListItem", "position": 1, "name": homeName, "item": "https://luxvono.com/" }
        ];

        if (pathname.startsWith('/category/')) {
            breadcrumbs.push({
                "@type": "ListItem",
                "position": 2,
                "name": pathname.replace('/category/', '').replace(/[-_]/g, ' '),
                "item": `https://luxvono.com${pathname}`
            });
        } else if (pathname !== '/') {
            breadcrumbs.push({
                "@type": "ListItem",
                "position": 2,
                "name": pageTitle.split('|')[0].trim(),
                "item": `https://luxvono.com${pathname}`
            });
        }

        breadcrumbJson.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbs
        });

    }, [location.pathname, location.search]);

    return null;
}
